import type {
  RestaurantMenu,
  CreateOrderRequest,
  CreateOrderResponse,
  CheckoutResponse,
  OrderStatusResponse,
} from './types'

export class NotFoundError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'NotFoundError'
  }
}

export class ServerError extends Error {
  constructor(status: number) {
    super(`Server error: ${status}`)
    this.name = 'ServerError'
  }
}

export class ValidationError extends Error {
  body: Record<string, string[]>

  constructor(body: Record<string, string[]>) {
    super('Validation error')
    this.name = 'ValidationError'
    this.body = body
  }
}

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, init)
  } catch {
    // No HTTP response at all — genuine network failure (offline, DNS, CORS
    // preflight failure, etc). Treat the same as an opaque server failure.
    throw new ServerError(0)
  }

  if (res.status === 404) {
    throw new NotFoundError(`Not found: ${path}`)
  }

  if (res.status === 400) {
    const body = (await res.json()) as Record<string, string[]>
    throw new ValidationError(body)
  }

  if (!res.ok) {
    throw new ServerError(res.status)
  }

  return res.json() as Promise<T>
}

export async function fetchMenu(slug: string): Promise<RestaurantMenu> {
  try {
    return await request<RestaurantMenu>(`${API_BASE_URL}/api/v1/restaurants/${slug}/menu/`)
  } catch (err) {
    if (err instanceof NotFoundError) {
      throw new NotFoundError(`Restaurant not found: ${slug}`)
    }
    throw err
  }
}

function postJSON<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

export function createOrder(slug: string, payload: CreateOrderRequest): Promise<CreateOrderResponse> {
  return postJSON<CreateOrderResponse>(`${API_BASE_URL}/api/v1/restaurants/${slug}/orders/`, payload)
}

export function fetchOrderStatus(orderId: string): Promise<OrderStatusResponse> {
  return request<OrderStatusResponse>(`${API_BASE_URL}/api/v1/orders/${orderId}/`)
}

// Also the initial checkout call, not just a retry — start_checkout() (KAI-300)
// reuses the existing Payment + idempotency key if one already exists for this
// order, so calling this again for the same order is always safe.
export function retryCheckout(orderId: string): Promise<CheckoutResponse> {
  return postJSON<CheckoutResponse>(`${API_BASE_URL}/api/v1/orders/${orderId}/checkout/`, {})
}
